import {Trans} from '@lingui/macro';
import {isEqual} from 'lodash';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {RadioButton} from '../../../libs/shared/ui/src/components/booleanInputs/radioButton';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {InlineBanner, InlineBannerTypesEnum} from '../../components/inlineBanner/inlineBanner';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {
  TeammateBasicNotificationSettingsEnum,
  TeammateBasicNotificationTypesEnum,
} from '../../models/api/notificationRulesModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsNotificationsUrl, SettingsTabsPagesEnum} from '../../models/url/settingsUrlModel';
import {dismissSettingsInfoBoxAction} from '../../store/data/localPreferences/localPreferencesActions';
import {updateUserNotificationRules} from '../../store/data/teammates/teammatesActions';
import {getUserNotificationRules} from '../../store/data/teammates/teammatesSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {
  SettingsFooter,
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsSidebar,
} from '../common/settingsWrapper';
import {SettingsNotificationsSidebar} from './settingsNotificationsSidebar';

/*
 * Props.
 */

interface SettingsBasicNotificationsProps {
  toggleSidebar: Handler;
  infoBoxId: string;
  isSidebarOpen: boolean;
  url: SettingsNotificationsUrl;
}

/*
 * Style.
 */

const StyledBasicNotificationsWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 24px;
  max-width: 600px;
  margin: 0 50px;
`;

const StyledBasicNotificationsMessageWrapper = styled.div`
  margin: 23px 50px;
  max-width: 600px;
`;

const StyledBasicNotificationsSectionDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StyledBasicNotificationsSectionTitle = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
`;

const StyledBasicNotificationsRadioGroup = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/*
 * Component.
 */

export const SettingsBasicNotifications: FC<SettingsBasicNotificationsProps> = (props) => {
  const {toggleSidebar, infoBoxId, isSidebarOpen} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const notificationRules = useAppSelector(getUserNotificationRules);
  const initialBasicNotificationRules = useMemo(() => notificationRules.basic, [notificationRules.basic]);
  const [basicNotificationRules, setBasicNotificationRules] = useState(initialBasicNotificationRules);
  const canSave = useMemo(
    () => !isEqual(initialBasicNotificationRules, basicNotificationRules),
    [basicNotificationRules, initialBasicNotificationRules],
  );
  const {showBanner} = useSettingsBanners();

  const saveBasicNotifications = () => {
    dispatch(
      updateUserNotificationRules({basic: basicNotificationRules, advanced: notificationRules.advanced}),
    );
    showBanner({
      message: <Trans id="settingsnotifications-notificationsupdated">Notifications updated.</Trans>,
      type: SettingsBannersEnum.SUCCESS,
    });
  };

  const onInfoBoxCloseClick = () => {
    toggleSidebar();
    dispatch(dismissSettingsInfoBoxAction(infoBoxId));
  };
  const onInfoBoxFooterClick = () => {
    openInBrowser('https://help.front.com/t/6329qd/how-to-change-my-notification-settings');
  };

  const shouldShowAdvancedRuleBanner = notificationRules.advanced.length > 0;

  const renderFooter = () => {
    if (!canSave) {
      return null;
    }
    return (
      <SettingsFooter>
        <Button
          isRounded
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() => {
            setBasicNotificationRules(initialBasicNotificationRules);
          }}
        >
          <Trans id="settings-vacations-responder-form-cancel">Cancel</Trans>
        </Button>
        <Button
          isRounded
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={saveBasicNotifications}
          isDisabled={!canSave}
        >
          <Trans id="settings-vacations-responder-form-save">Save</Trans>
        </Button>
      </SettingsFooter>
    );
  };

  return (
    <>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledBasicNotificationsWrapper>
            {collectEnumValues(TeammateBasicNotificationTypesEnum).map((value) => (
              <StyledBasicNotificationsSectionDiv key={value}>
                <StyledBasicNotificationsSectionTitle>
                  {sectionTitleRenderers[value]}
                </StyledBasicNotificationsSectionTitle>
                <StyledBasicNotificationsRadioGroup>
                  <RadioButton
                    onChange={() => {
                      setBasicNotificationRules((curr) => ({
                        ...curr,
                        [value]: TeammateBasicNotificationSettingsEnum.ALL,
                      }));
                    }}
                    isChecked={basicNotificationRules[value] === TeammateBasicNotificationSettingsEnum.ALL}
                  >
                    <Trans id="settingsnotifications-all">All notifications</Trans>
                  </RadioButton>
                  <RadioButton
                    onChange={() => {
                      setBasicNotificationRules((curr) => ({
                        ...curr,
                        [value]: TeammateBasicNotificationSettingsEnum.SMART,
                      }));
                    }}
                    isChecked={basicNotificationRules[value] === TeammateBasicNotificationSettingsEnum.SMART}
                  >
                    <Trans id="settingsnotifications-smart">Smart notifications</Trans>
                  </RadioButton>
                  <RadioButton
                    onChange={() => {
                      setBasicNotificationRules((curr) => ({
                        ...curr,
                        [value]: TeammateBasicNotificationSettingsEnum.NONE,
                      }));
                    }}
                    isChecked={basicNotificationRules[value] === TeammateBasicNotificationSettingsEnum.NONE}
                  >
                    <Trans id="settingsnotifications-no">No notifications</Trans>
                  </RadioButton>
                </StyledBasicNotificationsRadioGroup>
              </StyledBasicNotificationsSectionDiv>
            ))}
          </StyledBasicNotificationsWrapper>
          {shouldShowAdvancedRuleBanner && (
            <StyledBasicNotificationsMessageWrapper>
              <InlineBanner
                type={InlineBannerTypesEnum.INFO}
                size={VisualSizesEnum.SMALL}
                title={
                  <Trans id="settingsnotifications-advancedconflicts">
                    You have advanced notifications that may override your basic notifications
                  </Trans>
                }
                hasSmallBorderRadius
                hasInlineActions
                actions={[
                  {
                    id: 'view',
                    onClick: () => {
                      navigateToLink(
                        buildSettingsPath({
                          ...props.url,
                          tabPage: SettingsTabsPagesEnum.ADVANCED,
                        }),
                      );
                    },
                    title: <Trans id="settingsnotifications-view">View</Trans>,
                  },
                ]}
              />
            </StyledBasicNotificationsMessageWrapper>
          )}
        </StyledSettingsContent>
        {isSidebarOpen && (
          <StyledSettingsSidebar>
            <SettingsNotificationsSidebar
              isAdvancedView={props.url.tabPage === SettingsTabsPagesEnum.ADVANCED}
              onCloseClick={onInfoBoxCloseClick}
              onFooterClick={onInfoBoxFooterClick}
            />
          </StyledSettingsSidebar>
        )}
      </StyledSettingsMain>
      {renderFooter()}
    </>
  );
};

const sectionTitleRenderers: {[k in TeammateBasicNotificationTypesEnum]: ReactNode} = {
  [TeammateBasicNotificationTypesEnum.APP]: <Trans id="settingsnotifications-desktop">Desktop</Trans>,
  [TeammateBasicNotificationTypesEnum.MOBILE]: <Trans id="settingsnotifications-mobile">Mobile</Trans>,
  [TeammateBasicNotificationTypesEnum.EMAIL]: <Trans id="settingsnotifications-email">Email</Trans>,
};
